// TOKENOMIC section component - waiting for user-provided implementation.
// This file will be replaced with the final code the user sends.
import React, { useRef } from "react";
import { Link } from "react-router-dom";
import "./atom-tokenomics.css";

// --- 1. DATA REAL TOKENOMICS (Traducida y con Paleta Atom) ---
// Total: 100%
const tokenData = [
  // El 50% es enorme, usaremos el Azul Atom Principal para que domine
  { label: "Private Sale", value: 50.0, color: "#3B82F6" }, // Blue 500 (Atom Blue)

  // 15% CEX Listing - Cyan Eléctrico para destacar liquidez
  { label: "CEX Listing", value: 15.0, color: "#06B6D4" }, // Cyan 500

  // 10% Marketing - Un tono púrpura/azul oscuro para diferenciar
  { label: "Marketing", value: 10.0, color: "#4F46E5" }, // Indigo 600

  // Segmentos pequeños (5% cada uno) - Escala de grises y azules pálidos
  { label: "Reserve", value: 5.0, color: "#1F2937" }, // Gray 800 (Casi negro)
  { label: "Team (Locked)", value: 5.0, color: "#9CA3AF" }, // Gray 400
  { label: "Advisors", value: 5.0, color: "#CBD5E1" }, // Gray 300
  { label: "Development", value: 5.0, color: "#93C5FD" }, // Blue 300 (Soft Blue)
  { label: "Airdrop", value: 5.0, color: "#67E8F9" }, // Cyan 300 (Soft Cyan)
];

// --- FUNCIONES MATEMaTICAS SVG (Para dibujar el Donut y Lineas) ---
const polarToCartesian = (
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number,
) => {
  // Restamos 90 grados para empezar desde arriba (las 12 en punto)
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
};

const describeDonutSegment = (
  x: number,
  y: number,
  innerR: number,
  outerR: number,
  startAngle: number,
  endAngle: number,
) => {
  const startOuter = polarToCartesian(x, y, outerR, endAngle);
  const endOuter = polarToCartesian(x, y, outerR, startAngle);
  const startInner = polarToCartesian(x, y, innerR, endAngle);
  const endInner = polarToCartesian(x, y, innerR, startAngle);

  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

  return [
    "M",
    startOuter.x,
    startOuter.y,
    "A",
    outerR,
    outerR,
    0,
    largeArcFlag,
    0,
    endOuter.x,
    endOuter.y,
    "L",
    endInner.x,
    endInner.y,
    "A",
    innerR,
    innerR,
    0,
    largeArcFlag,
    1,
    startInner.x,
    startInner.y,
    "Z",
  ].join(" ");
};

const AtomTokenomics: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Configuracion de Dimensiones del Chart
  const size = 640;
  const cx = size / 2;
  const cy = size / 2;
  const outerRadius = 190; // Radio del anillo externo (reducido)
  const innerRadius = 110; // Radio del agujero
  const labelRadius = 280; // Distancia de las lineas y labels

  // Calculo de Segmentos
  let currentAngle = 0;
  const segments = tokenData.map((item) => {
    const angle = (item.value / 100) * 360;
    const start = currentAngle;
    const end = currentAngle + angle;
    // El angulo medio es clave para saber de donde sale la linea
    const middle = start + angle / 2;
    currentAngle += angle;
    return { ...item, start, end, middle };
  });

  return (
    <section className="atom-token-section" ref={containerRef}>
      <div className="atom-token-container">
        <div className="atom-token-title-wrapper">
          <h2 className="atom-token-title">Tokenomics</h2>
        </div>

        {/* --- CHART (Columna Izquierda) --- */}
        <div className="atom-chart-wrapper">
          <svg
            className="atom-chart-svg"
            viewBox={`0 0 ${size} ${size}`}
          >
            {/* Donut Segments */}
            <g className="chart-segments">
              {segments.map((seg, i) => (
                <path
                  key={i}
                  className="atom-chart-segment"
                  d={describeDonutSegment(
                    cx,
                    cy,
                    innerRadius,
                    outerRadius,
                    seg.start,
                    seg.end - 1.5, // gap
                  )}
                  fill={seg.color}
                />
              ))}
            </g>

            {/* Centro del Donut */}
            <g className="chart-center">
              <text x={cx} y={cy - 15} className="atom-center-text-label">
                Total Supply
              </text>
              <text x={cx} y={cy + 35} className="atom-center-text-value">
                600M
              </text>
            </g>

            {/* Conectores y Etiquetas */}
            <g className="chart-connectors">
              {segments.map((seg, i) => {
                const midAngle = seg.middle;
                const isLeft = midAngle > 180; // izquierda/derecha

                const p1 = polarToCartesian(cx, cy, outerRadius + 5, midAngle);
                const p2 = polarToCartesian(cx, cy, labelRadius, midAngle);

                const elbowLength = 50;
                const p3 = {
                  x: isLeft ? p2.x - elbowLength : p2.x + elbowLength,
                  y: p2.y,
                };

                return (
                  <g key={`con-${i}`}>
                    <polyline
                      points={`${p1.x},${p1.y} ${p2.x},${p2.y} ${p3.x},${p3.y}`}
                      fill="none"
                      stroke={seg.color}
                      strokeWidth="3"
                      className="atom-connector-line"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />

                    <g className="atom-chart-text-group">
                      {/* Porcentaje */}
                      <text
                        id={`percent-${i}`}
                        x={isLeft ? p3.x - 10 : p3.x + 10}
                        y={p3.y - 6}
                        textAnchor={isLeft ? "end" : "start"}
                        className="atom-chart-percent"
                      >
                        {seg.value.toFixed(1)}%
                      </text>

                      {/* Label */}
                      <text
                        x={isLeft ? p3.x - 10 : p3.x + 10}
                        y={p3.y + 14}
                        textAnchor={isLeft ? "end" : "start"}
                        className="atom-chart-label"
                      >
                        {seg.label}
                      </text>

                      {/* Punto Final */}
                      <circle cx={p3.x} cy={p3.y} r="3" fill={seg.color} />
                    </g>
                  </g>
                );
              })}
            </g>
          </svg>
        </div>

        {/* --- INFO CARD (Columna Derecha) --- */}
        <div className="atom-token-card">
          <div className="atom-card-header">
            <h3>Token Info</h3>
          </div>

          <div className="atom-token-list">
            <div className="atom-token-item">
              <span className="atom-item-label">Supply</span>
              <span className="atom-item-value">600,000,000</span>
            </div>

            <div className="atom-token-item">
              <span className="atom-item-label">Ticker</span>
              <span className="atom-item-value">$ATOM</span>
            </div>

            <div className="atom-token-item">
              <span className="atom-item-label">Network</span>
              <span className="atom-item-value atom-net-badge">
                <svg width="14" height="22" viewBox="0 0 14 22" fill="none">
                  <path
                    d="M6.999 0L6.764 0.776V15.76L6.999 15.991L13.999 11.956L6.999 0Z"
                    fill="#3B82F6"
                  />
                  <path
                    d="M6.999 0L0 11.956L6.999 15.991V8.718V0Z"
                    fill="#3B82F6"
                    fillOpacity="0.8"
                  />
                  <path
                    d="M6.999 17.435L6.873 17.587V21.739L6.999 22.096L14 12.35L6.999 17.435Z"
                    fill="#3B82F6"
                  />
                  <path
                    d="M6.999 22.096V17.434L0 12.35L6.999 22.096Z"
                    fill="#3B82F6"
                    fillOpacity="0.8"
                  />
                </svg>
                ETH (ERC-20)
              </span>
            </div>

            <div className="atom-token-item">
              <span className="atom-item-label">Contract</span>
              <a
                className="atom-item-value atom-contract-link"
                href="https://etherscan.io/token/0x85c9793Ac3e8328ddEE86590A015717D63C3d348"
                target="_blank"
                rel="noopener noreferrer"
              >
                0x8***348
              </a>
            </div>

            <div className="atom-token-item">
              <span className="atom-item-label">Whitelist Price</span>
              <span className="atom-item-value">$0.05</span>
            </div>

            <div className="atom-token-item">
              <span className="atom-item-label">Public Price</span>
              <span className="atom-item-value">$0.50</span>
            </div>

            <div className="atom-token-item">
              <span className="atom-item-label">FDV</span>
              <span className="atom-item-value">$300M</span>
            </div>
          </div>

          <div className="atom-token-cta-wrapper">
            <Link className="thm-btn agency-btn" to="https://atom-foundation.gitbook.io/atom-foundation-docs/tokenomics-and-distribution">
              <span className="text">DETAILED TOKENOMIC</span>
              <span className="arrow">
                <span className="arrow-icon">
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 28 28"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="5.06592"
                      y="19.9785"
                      width="20.5712"
                      height="2.61221"
                      transform="rotate(-40.2798 5.06592 19.9785)"
                      fill="white"
                    />
                    <rect
                      x="7.97095"
                      y="7.24463"
                      width="2.61221"
                      height="2.61221"
                      transform="rotate(-40.2798 7.97095 7.24463)"
                      fill="white"
                    />
                    <rect
                      x="11.6523"
                      y="7.54834"
                      width="2.61221"
                      height="2.61221"
                      transform="rotate(-40.2798 11.6523 7.54834)"
                      fill="white"
                    />
                    <rect
                      x="15.334"
                      y="7.85205"
                      width="2.61221"
                      height="2.61221"
                      transform="rotate(-40.2798 15.334 7.85205)"
                      fill="white"
                    />
                    <rect
                      x="18.7119"
                      y="11.8374"
                      width="2.61221"
                      height="2.61221"
                      transform="rotate(-40.2798 18.7119 11.8374)"
                      fill="white"
                    />
                    <rect
                      x="18.4084"
                      y="15.52"
                      width="2.61221"
                      height="2.61221"
                      transform="rotate(-40.2798 18.4084 15.52)"
                      fill="white"
                    />
                    <rect
                      x="18.104"
                      y="19.2012"
                      width="2.61221"
                      height="2.61221"
                      transform="rotate(-40.2798 18.104 19.2012)"
                      fill="white"
                    />
                  </svg>
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 28 28"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="5.06592"
                      y="19.9785"
                      width="20.5712"
                      height="2.61221"
                      transform="rotate(-40.2798 5.06592 19.9785)"
                      fill="white"
                    />
                    <rect
                      x="7.97095"
                      y="7.24463"
                      width="2.61221"
                      height="2.61221"
                      transform="rotate(-40.2798 7.97095 7.24463)"
                      fill="white"
                    />
                    <rect
                      x="11.6523"
                      y="7.54834"
                      width="2.61221"
                      height="2.61221"
                      transform="rotate(-40.2798 11.6523 7.54834)"
                      fill="white"
                    />
                    <rect
                      x="15.334"
                      y="7.85205"
                      width="2.61221"
                      height="2.61221"
                      transform="rotate(-40.2798 15.334 7.85205)"
                      fill="white"
                    />
                    <rect
                      x="18.7119"
                      y="11.8374"
                      width="2.61221"
                      height="2.61221"
                      transform="rotate(-40.2798 18.7119 11.8374)"
                      fill="white"
                    />
                    <rect
                      x="18.4084"
                      y="15.52"
                      width="2.61221"
                      height="2.61221"
                      transform="rotate(-40.2798 18.4084 15.52)"
                      fill="white"
                    />
                    <rect
                      x="18.104"
                      y="19.2012"
                      width="2.61221"
                      height="2.61221"
                      transform="rotate(-40.2798 18.104 19.2012)"
                      fill="white"
                    />
                  </svg>
                </span>
              </span>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AtomTokenomics;
